/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { CoreApiService } from '../core-api-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { closeRouterLink } from '../fn/srs-client/close-router-link';
import { CloseRouterLink$Params } from '../fn/srs-client/close-router-link';
import { getLatestConfig } from '../fn/srs-client/get-latest-config';
import { GetLatestConfig$Params } from '../fn/srs-client/get-latest-config';
import { getOpenVpnConfiguration } from '../fn/srs-client/get-open-vpn-configuration';
import { GetOpenVpnConfiguration$Params } from '../fn/srs-client/get-open-vpn-configuration';
import { getRequestedSerialNumber } from '../fn/srs-client/get-requested-serial-number';
import { GetRequestedSerialNumber$Params } from '../fn/srs-client/get-requested-serial-number';
import { getStreamTicket } from '../fn/srs-client/get-stream-ticket';
import { GetStreamTicket$Params } from '../fn/srs-client/get-stream-ticket';
import { requestRouterLink } from '../fn/srs-client/request-router-link';
import { RequestRouterLink$Params } from '../fn/srs-client/request-router-link';
import { routerStatus } from '../fn/srs-client/router-status';
import { RouterStatus$Params } from '../fn/srs-client/router-status';
import { RouterStatusDto } from '../models/router-status-dto';
import { saveConfig } from '../fn/srs-client/save-config';
import { SaveConfig$Params } from '../fn/srs-client/save-config';
import { SrsClientConfigDto } from '../models/srs-client-config-dto';
import { SrsRouterLinkDto } from '../models/srs-router-link-dto';
import { startFirmwareUpdate } from '../fn/srs-client/start-firmware-update';
import { StartFirmwareUpdate$Params } from '../fn/srs-client/start-firmware-update';
import { uploadCertificateUpdate } from '../fn/srs-client/upload-certificate-update';
import { UploadCertificateUpdate$Params } from '../fn/srs-client/upload-certificate-update';
import { uploadFirmwareUpdate } from '../fn/srs-client/upload-firmware-update';
import { UploadFirmwareUpdate$Params } from '../fn/srs-client/upload-firmware-update';

@Injectable({ providedIn: 'root' })
export class SrsClientAPI extends CoreApiService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `closeRouterLink()` */
  static readonly CloseRouterLinkPath = '/api/srsc/closeRouterLink';

  /**
   * Close the previously established router link.
   *
   * Close the previously established link to a router
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `closeRouterLink()` instead.
   *
   * This method doesn't expect any request body.
   */
  closeRouterLink$Response(params?: CloseRouterLink$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return closeRouterLink(this.http, this.rootUrl, params, context);
  }

  /**
   * Close the previously established router link.
   *
   * Close the previously established link to a router
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `closeRouterLink$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  closeRouterLink(params?: CloseRouterLink$Params, context?: HttpContext): Observable<void> {
    return this.closeRouterLink$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `getLatestConfig()` */
  static readonly GetLatestConfigPath = '/api/srsc/getLatestConfig';

  /**
   * Returns the latest port forward config for the SRSClient.
   *
   * The srs client asks for the latest config on startup.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getLatestConfig()` instead.
   *
   * This method doesn't expect any request body.
   */
  getLatestConfig$Response(params?: GetLatestConfig$Params, context?: HttpContext): Observable<StrictHttpResponse<SrsClientConfigDto>> {
    return getLatestConfig(this.http, this.rootUrl, params, context);
  }

  /**
   * Returns the latest port forward config for the SRSClient.
   *
   * The srs client asks for the latest config on startup.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getLatestConfig$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getLatestConfig(params?: GetLatestConfig$Params, context?: HttpContext): Observable<SrsClientConfigDto> {
    return this.getLatestConfig$Response(params, context).pipe(
      map((r: StrictHttpResponse<SrsClientConfigDto>): SrsClientConfigDto => r.body)
    );
  }

  /** Path part for operation `getOpenVpnConfiguration()` */
  static readonly GetOpenVpnConfigurationPath = '/api/srsc/openvpnConfiguration';

  /**
   * Returns the OpenVPN configuration.
   *
   * The OpenVPN configuration will be returned as Text/Plain
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getOpenVpnConfiguration()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOpenVpnConfiguration$Response(params?: GetOpenVpnConfiguration$Params, context?: HttpContext): Observable<StrictHttpResponse<string>> {
    return getOpenVpnConfiguration(this.http, this.rootUrl, params, context);
  }

  /**
   * Returns the OpenVPN configuration.
   *
   * The OpenVPN configuration will be returned as Text/Plain
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getOpenVpnConfiguration$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOpenVpnConfiguration(params?: GetOpenVpnConfiguration$Params, context?: HttpContext): Observable<string> {
    return this.getOpenVpnConfiguration$Response(params, context).pipe(
      map((r: StrictHttpResponse<string>): string => r.body)
    );
  }

  /** Path part for operation `requestRouterLink()` */
  static readonly RequestRouterLinkPath = '/api/srsc/requestRouterLink';

  /**
   * Request a link to a router by serial number.
   *
   * Request a link to a router by the given serial number.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `requestRouterLink()` instead.
   *
   * This method sends `application/x-www-form-urlencoded` and handles request body of type `application/x-www-form-urlencoded`.
   */
  requestRouterLink$Response(params?: RequestRouterLink$Params, context?: HttpContext): Observable<StrictHttpResponse<SrsRouterLinkDto>> {
    return requestRouterLink(this.http, this.rootUrl, params, context);
  }

  /**
   * Request a link to a router by serial number.
   *
   * Request a link to a router by the given serial number.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `requestRouterLink$Response()` instead.
   *
   * This method sends `application/x-www-form-urlencoded` and handles request body of type `application/x-www-form-urlencoded`.
   */
  requestRouterLink(params?: RequestRouterLink$Params, context?: HttpContext): Observable<SrsRouterLinkDto> {
    return this.requestRouterLink$Response(params, context).pipe(
      map((r: StrictHttpResponse<SrsRouterLinkDto>): SrsRouterLinkDto => r.body)
    );
  }

  /** Path part for operation `getRequestedSerialNumber()` */
  static readonly GetRequestedSerialNumberPath = '/api/srsc/requestedSerialNumber';

  /**
   * Returns the serial number of requested router.
   *
   * The serial number of the requested router as Text/Plain
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getRequestedSerialNumber()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRequestedSerialNumber$Response(params?: GetRequestedSerialNumber$Params, context?: HttpContext): Observable<StrictHttpResponse<string>> {
    return getRequestedSerialNumber(this.http, this.rootUrl, params, context);
  }

  /**
   * Returns the serial number of requested router.
   *
   * The serial number of the requested router as Text/Plain
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getRequestedSerialNumber$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRequestedSerialNumber(params?: GetRequestedSerialNumber$Params, context?: HttpContext): Observable<string> {
    return this.getRequestedSerialNumber$Response(params, context).pipe(
      map((r: StrictHttpResponse<string>): string => r.body)
    );
  }

  /** Path part for operation `routerStatus()` */
  static readonly RouterStatusPath = '/api/srsc/routerStatus';

  /**
   * Returns the last router status from memory.
   *
   * The router sends on a regular bases a status event, the last received by the server is here returned
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `routerStatus()` instead.
   *
   * This method doesn't expect any request body.
   */
  routerStatus$Response(params: RouterStatus$Params, context?: HttpContext): Observable<StrictHttpResponse<RouterStatusDto>> {
    return routerStatus(this.http, this.rootUrl, params, context);
  }

  /**
   * Returns the last router status from memory.
   *
   * The router sends on a regular bases a status event, the last received by the server is here returned
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `routerStatus$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  routerStatus(params: RouterStatus$Params, context?: HttpContext): Observable<RouterStatusDto> {
    return this.routerStatus$Response(params, context).pipe(
      map((r: StrictHttpResponse<RouterStatusDto>): RouterStatusDto => r.body)
    );
  }

  /** Path part for operation `saveConfig()` */
  static readonly SaveConfigPath = '/api/srsc/saveConfig';

  /**
   * Persists the port forward configuration of the SRSCLient.
   *
   * The SRSClient sends the currently configured port forwards that are persisted here
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `saveConfig()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  saveConfig$Response(params?: SaveConfig$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return saveConfig(this.http, this.rootUrl, params, context);
  }

  /**
   * Persists the port forward configuration of the SRSCLient.
   *
   * The SRSClient sends the currently configured port forwards that are persisted here
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `saveConfig$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  saveConfig(params?: SaveConfig$Params, context?: HttpContext): Observable<void> {
    return this.saveConfig$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `startFirmwareUpdate()` */
  static readonly StartFirmwareUpdatePath = '/api/srsc/startFirmwareUpdate';

  /**
   * Start the firmware update.
   *
   * Approve the start of the firmware update on the router.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `startFirmwareUpdate()` instead.
   *
   * This method doesn't expect any request body.
   */
  startFirmwareUpdate$Response(params?: StartFirmwareUpdate$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return startFirmwareUpdate(this.http, this.rootUrl, params, context);
  }

  /**
   * Start the firmware update.
   *
   * Approve the start of the firmware update on the router.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `startFirmwareUpdate$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  startFirmwareUpdate(params?: StartFirmwareUpdate$Params, context?: HttpContext): Observable<void> {
    return this.startFirmwareUpdate$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `uploadCertificateUpdate()` */
  static readonly UploadCertificateUpdatePath = '/api/srsc/uploadCertificateUpdate';

  /**
   * Start the certificate upload.
   *
   * Approve the start of the certificate upload to the router.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `uploadCertificateUpdate()` instead.
   *
   * This method doesn't expect any request body.
   */
  uploadCertificateUpdate$Response(params?: UploadCertificateUpdate$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return uploadCertificateUpdate(this.http, this.rootUrl, params, context);
  }

  /**
   * Start the certificate upload.
   *
   * Approve the start of the certificate upload to the router.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `uploadCertificateUpdate$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  uploadCertificateUpdate(params?: UploadCertificateUpdate$Params, context?: HttpContext): Observable<void> {
    return this.uploadCertificateUpdate$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `uploadFirmwareUpdate()` */
  static readonly UploadFirmwareUpdatePath = '/api/srsc/uploadFirmwareUpdate';

  /**
   * Upload firmware to the router.
   *
   * Approve the upload of the firmware to the router. Afterwards the update of the firmware on the router can be started.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `uploadFirmwareUpdate()` instead.
   *
   * This method doesn't expect any request body.
   */
  uploadFirmwareUpdate$Response(params?: UploadFirmwareUpdate$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return uploadFirmwareUpdate(this.http, this.rootUrl, params, context);
  }

  /**
   * Upload firmware to the router.
   *
   * Approve the upload of the firmware to the router. Afterwards the update of the firmware on the router can be started.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `uploadFirmwareUpdate$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  uploadFirmwareUpdate(params?: UploadFirmwareUpdate$Params, context?: HttpContext): Observable<void> {
    return this.uploadFirmwareUpdate$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `getStreamTicket()` */
  static readonly GetStreamTicketPath = '/routers/{serialNumber}/ticket';

  /**
   * Fetch all opened streams for a given M2MDevice.
   *
   * Sends request to all cascade cluster nodes and requests the current opened Streams. Filters this for given device.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getStreamTicket()` instead.
   *
   * This method doesn't expect any request body.
   */
  getStreamTicket$Response(params: GetStreamTicket$Params, context?: HttpContext): Observable<StrictHttpResponse<string>> {
    return getStreamTicket(this.http, this.rootUrl, params, context);
  }

  /**
   * Fetch all opened streams for a given M2MDevice.
   *
   * Sends request to all cascade cluster nodes and requests the current opened Streams. Filters this for given device.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getStreamTicket$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getStreamTicket(params: GetStreamTicket$Params, context?: HttpContext): Observable<string> {
    return this.getStreamTicket$Response(params, context).pipe(
      map((r: StrictHttpResponse<string>): string => r.body)
    );
  }

}
